<script lang="ts">
  import {
    Button,
    FileUploaderDropContainer,
    FileUploaderItem,
    InlineNotification,
    ProgressBar,
  } from "carbon-components-svelte";
  import type APIClient from "./../../ts/APIClient";
  import type { IFileUploadDefaults } from "./../../ts/Interfaces";
  import { FileUploadProgress } from "./../../ts/Classes";
  import type { FileUploadTrack } from "./../../ts/Types";
  import { optionsData } from "./../../ts/Stores";

  export let client: APIClient;
  export let canvasWidth: number;
  export let canvasHeight: number;
  export let uploadedFileIds: string[] = [];

  let fileUploadDefaults: IFileUploadDefaults;
  optionsData.subscribe((optionsData) => {
    fileUploadDefaults = optionsData.fileUploadDefaults;
  });

  let multiple = fileUploadDefaults.allowMultiple;
  let accept = fileUploadDefaults.allowedFileTypes;
  let maxFileSizeKB = fileUploadDefaults.sizeLimitInKB;

  let maxFileSizeMB = maxFileSizeKB / 1024;
  let maxFileSizeBytes = maxFileSizeKB * 1024;

  let uploadProgress: FileUploadProgress = new FileUploadProgress();

  function loadstartHandler(lengthComputable: boolean, total: number): void {
    if (lengthComputable) {
      uploadProgress = uploadProgress
        .showControl()
        .updateProgress(
          0,
          total,
          `${Math.ceil(0 / 1024)}kb of ${Math.ceil(
            total / 1024
          )}kb (${Math.ceil((0 / total) * 100)}%)`
        );
    }
  }

  function progressHandler(
    lengthComputable: boolean,
    loaded: number,
    total: number
  ): void {
    if (lengthComputable) {
      uploadProgress = uploadProgress.updateProgress(
        loaded,
        total,
        `${Math.ceil(loaded / 1024)}kb of ${Math.ceil(
          total / 1024
        )}kb (${Math.ceil((loaded / total) * 100)}%)`
      );
    }
  }

  let abortFn: () => void;

  function setAbortFn(abort: () => void) {
    abortFn = abort;
  }

  function abortHandler(eventType: string) {
    uploadProgress = uploadProgress.updateStatus("error", "Upload aborted");
  }

  let resetControl = () => {
    uploadedFileIds = [];
    uploadProgress = uploadProgress.reset();
  };

  let uploadFiles = async (files: readonly File[]) => {
    resetControl();

    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      let cFile: FileUploadTrack = {
        file: files[i],
        status: "uploading",
        invalid: false,
        errorSubject: null,
        errorBody: null,
      };

      if (!accept.includes(getFileExtension(cFile.file))) {
        cFile.status = "edit";
        cFile.invalid = true;
        cFile.errorSubject = `File type not allowed`;
        cFile.errorBody = `Allowed file types are ${accept.join(
          ","
        )}. Please select a new file.`;
      }

      if (!cFile.invalid && cFile.file.size > maxFileSizeBytes) {
        cFile.status = "edit";
        cFile.invalid = true;
        cFile.errorSubject = `File size exceeds ${maxFileSizeMB}MB limit`;
        cFile.errorBody = `Maximum file size is ${Math.ceil(
          maxFileSizeMB
        )}MB. Please select a new file.`;
      }

      uploadProgress = uploadProgress.addFile(cFile);

      if (!cFile.invalid) {
        formData.append("files", cFile.file);
      }
    }

    if (Array.from(formData.entries()).length <= 0) return;

    await client
      .uploadFiles(
        canvasWidth,
        canvasHeight,
        formData,
        loadstartHandler,
        progressHandler,
        setAbortFn,
        abortHandler
      )
      .then((response) => {
        uploadProgress = uploadProgress.updateStatus(
          "finished",
          "Files uploaded successfully."
        );
        uploadedFileIds = response["ids"];
      })
      .catch((reason) => {
        uploadProgress = uploadProgress.updateStatus(
          "error",
          `Upload failed (${reason}).`
        );
      });
  };

  let getFileExtension = (file: File) => {
    const filenameParts = file.name.split(".");
    return `.${filenameParts[filenameParts.length - 1].toLowerCase()}`;
  };

  // let validateFiles = (files: File[]) => {
  //   return files.filter(
  //     (file) =>
  //       file.size < maxFileSize && accept.includes(getFileExtension(file))
  //   );
  // };
</script>

<InlineNotification
  kind="warning"
  title="Note"
  subtitle="Only {fileUploadDefaults.allowedFileTypes
    .join(',')
    .toUpperCase()} files are accepted. Size must be 
        {Math.ceil(fileUploadDefaults.sizeLimitInKB / 1024)} MB or less."
/>
{#if uploadProgress.displayControl}
  <ProgressBar
    labelText="Upload status"
    value={uploadProgress.value}
    max={uploadProgress.max}
    helperText={uploadProgress.helperText}
    status={uploadProgress.status}
  />
  {#if uploadProgress.status == "active"}
    <Button
      kind="danger-ghost"
      size="small"
      on:click={abortFn}
      class="control-b-margin"
    >
      Cancel Upload
    </Button>
  {/if}
{/if}
<FileUploaderDropContainer
  {multiple}
  {accept}
  labelText="Drag and drop files here or click to upload."
  on:change={(e) => {
    uploadFiles(e.detail);
  }}
/>
{#each uploadProgress.fileTrack as cFile}
  <FileUploaderItem
    name={`${cFile.file.name} ~ ${Math.ceil(cFile.file.size / 1024)} Kb`}
    invalid={cFile.invalid}
    errorSubject={cFile.errorSubject}
    errorBody={cFile.errorBody}
    status={cFile.status}
    on:delete={(e) => {
      uploadProgress = uploadProgress.removeFile(cFile.file.name);
    }}
  />
{/each}
